// web-frontend/src/App.jsx
// 🔥🔥🔥 2025-12-16 수정: 스플래시 화면 추가
// 🔥🔥🔥 2025-12-19 수정: ThemeProvider 추가
// 🔥🔥🔥 2025-12-20 수정: WebSocket 재연결 핸들러 추가
// 🔥🔥🔥 2025-12-22 수정: AppSettingsProvider 추가 (폰트 크기, 데이터 절약 모드)
// 🔥🔥🔥 2026-01-22 수정: SurveyReminderModal 제거 - MainLayout의 설문 모달만 사용
import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import AppRoutes from './routes';
import apiClient from './shared/services/apiClient';
import authService from './shared/services/authService';
import chatsService from './shared/services/chatsService';
import { validateStoredToken } from './shared/utils/storage';
import useAuthStore from './shared/stores/authStore';
import useAppStore from './shared/stores/appStore';
import useChatsStore from './shared/stores/chatsStore';
import i18nService from './shared/services/i18n';
import { ToastProvider } from './components/common/Toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppSettingsProvider } from './contexts/AppSettingsContext';
import SplashScreen from './components/common/SplashScreen';
import YearConsentModal from './components/common/YearConsentModal';
import ErrorBoundary from './components/common/ErrorBoundary';
import analyticsService from './shared/services/analyticsService';
import logger from './shared/utils/logger';
import { getDefaultLanguage } from './shared/config/tenant';
import './styles/global.css';

const INIT_TIMEOUT = 15000;

function App() {
  const [isAppReady, setIsAppReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const [initProgress, setInitProgress] = useState('시작 중...');
  const [showSplash, setShowSplash] = useState(true);
  
  // Zustand store
  const { user, verifyToken, logout } = useAuthStore();
  const appStore = useAppStore();
  const { setLanguage } = appStore;

  // =========================================================================
  // 언어 설정 동기화 — 로컬(사용자가 명시한) 언어 우선, 서버(preferred_language)는
  // 보조. 이전 구현은 로그인/유저 갱신 때마다 서버 값을 강제 적용해서, 사용자가
  // 온보딩 중 영어로 바꿔도 다음 user state 갱신(예: 설문 제출 후 carbon point
  // updateUser) 에서 서버의 'ko' 가 다시 덮어쓰는 회귀가 있었다.
  // 새 정책:
  //   1) 로그인 직후 (isNewLogin=true) 에만 동기화 결정을 한다.
  //   2) 로컬 언어가 명시 설정되어 있으면 로컬이 이긴다. 서버가 다르면 서버를
  //      로컬에 맞춘다 (best-effort).
  //   3) 로컬이 비어 있고 서버에만 값이 있으면 서버 값을 1회 적용.
  //   4) 같은 사용자 user state 갱신(은닉 fetch 등)에서는 언어를 절대 건드리지
  //      않는다.
  // =========================================================================
  const prevUserIdRef = React.useRef(null);
  useEffect(() => {
    if (!user) return;
    const isNewLogin = prevUserIdRef.current !== user.id;
    prevUserIdRef.current = user.id;

    if (!isNewLogin) return; // 동일 사용자 — 로컬 언어 유지

    const userLang = user.preferred_language;
    const localLang = i18nService.getCurrentLanguage();

    if (localLang) {
      // 로컬 언어 우선 — 서버가 다르면 서버를 로컬에 맞춤 (조용히 실패 OK)
      if (userLang && userLang !== localLang) {
        authService.updateProfile({ preferred_language: localLang }).catch((e) => {
          logger.warn('⚠️ [App] 서버 preferred_language 동기화 실패 (무시):', e);
        });
      }
    } else if (userLang) {
      // 로컬은 비어있고 서버에 값 — 서버 값 1회 적용
      i18nService.changeLanguage(userLang);
      setLanguage(userLang);
    }
    analyticsService.setLanguage(localLang || userLang || getDefaultLanguage() || 'ko');
  }, [user?.id, setLanguage]);

  // =========================================================================
  // 🔥🔥🔥 [추가] WebSocket 재연결 시 채팅방 목록 갱신 핸들러
  // =========================================================================
  useEffect(() => {
    const handleReconnection = (data) => {
      logger.debug('🔄 [App] WebSocket 재연결 감지:', data);
      
      if (useChatsStore.getState().handleWebSocketReconnected) {
        useChatsStore.getState().handleWebSocketReconnected();
      }
    };
    
    const chatRoomHandler = (event) => {
      if (event.type === 'reconnected') {
        handleReconnection(event);
      }
    };
    
    chatsService.addChatRoomUpdateHandler(chatRoomHandler);
    chatsService.addReconnectionHandler(handleReconnection);
    
    return () => {
      chatsService.removeChatRoomUpdateHandler(chatRoomHandler);
      chatsService.removeReconnectionHandler(handleReconnection);
    };
  }, []);

  // 🔥🔥🔥 [2026-01-22 제거] 설문 리마인더 WebSocket 핸들러 제거
  // MainLayout에서 설문 모달을 통합 관리하므로 더 이상 필요 없음

  // =========================================================================
  // 앱 초기화
  // =========================================================================
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      logger.debug('🚀 웹 앱 초기화 시작...');
      
      const timeout = setTimeout(() => {
        logger.warn('⏰ 초기화 타임아웃 - 강제로 앱 시작');
        forceCompleteInit('초기화 시간 초과');
      }, INIT_TIMEOUT);
      
      // 🔥 0단계: AppStore 초기화 (언어, 테마 복구) - 최우선 실행
      setInitProgress('앱 설정 불러오는 중...');
      logger.debug('🌐 AppStore 초기화 시작 (언어/테마 복구)');
      await appStore.initializeApp();
      logger.debug('✅ AppStore 초기화 완료');
      
      // 🔥 [신규] Analytics 서비스 초기화
      analyticsService.initialize({
        language: i18nService.getCurrentLanguage() || appStore.language || getDefaultLanguage() || 'ko',
        enabled: true,
      });
      logger.debug('📊 Analytics 서비스 초기화 완료');
      
      // 🔥 authService에 authStore 연결
      authService.setAuthStore(useAuthStore);
      
      // 🔥 apiClient에 인증 에러 핸들러 설정
      apiClient.setAuthErrorHandler(() => {
        logger.debug('🚪 [App] 인증 에러 - 자동 로그아웃');
        logout();
      });
      
      // 1단계: 토큰 검증 및 만료된 데이터 클리어
      setInitProgress('인증 정보 검증 중...');
      const tokenValidation = await validateStoredToken();
      
      if (tokenValidation.valid) {
        logger.debug('✅ 유효한 토큰 발견 - 추가 검증 진행');
        await verifyToken();
      } else {
        logger.debug('ℹ️ 저장된 토큰 없음 또는 만료됨');
      }
      
      // 2단계: API 클라이언트 초기화
      setInitProgress('API 클라이언트 초기화 중...');
      await apiClient.initialize();
      
      clearTimeout(timeout);
      
      logger.debug('✅ 웹 앱 초기화 완료');
      setIsAppReady(true);
    } catch (error) {
      logger.error('❌ 앱 초기화 중 오류 발생:', error);
      setInitError(error);
      setIsAppReady(true);
    }
  };

  const forceCompleteInit = (reason = '사용자 요청') => {
    logger.debug('⚡ 강제 초기화 완료: ' + reason);
    setIsAppReady(true);
  };

  // 🚀 스플래시 완료 핸들러
  const handleSplashComplete = () => {
    logger.debug('🎬 스플래시 화면 완료');
    setShowSplash(false);
  };

  // 🚀 앱이 준비되지 않았을 때 로딩 화면 표시
  if (!isAppReady) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>{initProgress}</p>
        <p style={styles.loadingSubText}>앱을 준비 중입니다...</p>
        <button
          style={styles.skipButton}
          onClick={() => forceCompleteInit('사용자가 스킵')}
        >
          스킵하고 계속하기
        </button>
      </div>
    );
  }

  if (initError) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorIcon}>!</div>
        <p style={styles.loadingText}>초기화 중 문제가 발생했습니다</p>
        <p style={styles.loadingSubText}>{initError.message || '네트워크 연결을 확인해 주세요.'}</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            style={styles.skipButton}
            onClick={() => {
              setInitError(null);
              setIsAppReady(false);
              setShowSplash(true);
              initializeApp();
            }}
          >
            다시 시도
          </button>
          <button
            style={{ ...styles.skipButton, backgroundColor: 'transparent', border: '1px solid #333' }}
            onClick={() => setInitError(null)}
          >
            그냥 계속하기
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppSettingsProvider>
          <ToastProvider>
            {showSplash && (
              <SplashScreen onComplete={handleSplashComplete} />
            )}

            <BrowserRouter>
              <div style={{
                opacity: showSplash ? 0 : 1,
                transition: 'opacity 0.3s ease-in',
                visibility: showSplash ? 'hidden' : 'visible'
              }}>
                <AppRoutes />
              </div>
              {/* 🔥 [2026-10-23] 그해 첫 로그인 동의 다시 확인 */}
              {!showSplash && <YearConsentModal />}
            </BrowserRouter>
          </ToastProvider>
        </AppSettingsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    // 🎨 [2026-05-22] 로딩 화면도 테넌트 brand 적용 — primary(네이비) 가 어두운 톤이면 그대로,
    //   라이트 톤이면 어두운 fallback. CSS 변수가 없는 시점 (앱 부팅 0~1초) 에도 안전한 fallback.
    backgroundColor: 'var(--color-primary-dark, var(--color-primary, #052243))',
    padding: '20px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid rgba(255,255,255,0.18)',
    borderTop: '5px solid var(--color-secondary, #fbba21)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  errorIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-status-error, #D32F2F)',
    color: '#FFFFFF',
    fontSize: '28px',
    fontWeight: '700',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '12px',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
    color: 'rgba(255,255,255,0.85)',
    fontWeight: '600',
  },
  loadingSubText: {
    marginTop: '8px',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
  },
  skipButton: {
    marginTop: '40px',
    padding: '12px 24px',
    backgroundColor: 'var(--color-secondary, #fbba21)',
    color: 'var(--color-primary-dark, #031530)',
    border: 'none',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
    transition: 'all 0.2s',
  },
};

const globalStyles = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
}

button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0,0,0,0.25);
}
`;

if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

export default App;
